import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { getPaymentEnvSnapshot } from "./envSanityCheck";

type AnyObj = Record<string, any>;

const truncate = (val: unknown, max = 4000): unknown => {
  if (typeof val === "string") return val.length > max ? val.slice(0, max) : val;
  return val;
};

const SKIP_KEYS = new Set(["__proto__"]);

const safeSerializeError = (err: any, depth = 0): AnyObj => {
  const out: AnyObj = {};
  if (err == null) return { message: String(err) };
  if (typeof err !== "object") return { message: String(err) };
  if (depth > 3) return { _truncated: "max depth" };

  const keys: string[] = Object.getOwnPropertyNames(err).concat(Object.keys(err));
  const seen: Record<string, true> = {};
  for (const key of keys) {
    if (seen[key]) continue;
    seen[key] = true;
    if (SKIP_KEYS.has(key)) continue;
    try {
      const v = (err as AnyObj)[key];
      if (v === undefined) continue;
      if (v === null) { out[key] = null; continue; }
      if (typeof v === "function") continue;
      if (typeof v === "bigint") { out[key] = v.toString(); continue; }
      if (typeof v === "object") {
        if (v instanceof Error || (v && typeof (v as any).message === "string" && depth < 3)) {
          out[key] = safeSerializeError(v, depth + 1);
        } else {
          out[key] = JSON.parse(
            JSON.stringify(v, (_k, val) => {
              if (typeof val === "bigint") return val.toString();
              if (typeof val === "function") return undefined;
              return val;
            })
          );
        }
      } else {
        out[key] = truncate(v);
      }
    } catch {
      out[key] = "[unserializable]";
    }
  }

  if (!out.message && err?.toString) {
    try { out.message = truncate(err.toString()); } catch { /* noop */ }
  }
  return out;
};

const stripUndefined = (obj: AnyObj): AnyObj => {
  const out: AnyObj = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined) continue;
    out[k] = v;
  }
  return out;
};

// ───────────────────────────────────────────────────────────────────
// Local replay queue
//
// Errors are persisted to localStorage BEFORE any network attempt so
// that nothing is lost when every transport fails silently — offline,
// iOS Safari ITP blocking the Firestore WebChannel, or the tab being
// closed before keepalive flushes. The queue is drained on the next
// app boot, on `online`, and whenever the page becomes visible again.
// ───────────────────────────────────────────────────────────────────

const QUEUE_KEY = "v2e:paymentErrorQueue";
const QUEUE_MAX = 50;
const QUEUE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const NET_TIMEOUT_MS = 5000;

const safeStorage = (): Storage | null => {
  try {
    if (typeof window === "undefined") return null;
    const s = window.localStorage;
    s.getItem(QUEUE_KEY);
    return s;
  } catch {
    return null;
  }
};

const readQueue = (): AnyObj[] => {
  const s = safeStorage();
  if (!s) return [];
  try {
    const raw = s.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeQueue = (q: AnyObj[]): void => {
  const s = safeStorage();
  if (!s) return;
  try {
    s.setItem(QUEUE_KEY, JSON.stringify(q));
  } catch {
    // Quota exceeded — drop oldest half and retry once.
    try {
      s.setItem(QUEUE_KEY, JSON.stringify(q.slice(Math.floor(q.length / 2))));
    } catch { /* give up */ }
  }
};

const enqueue = (entry: AnyObj): void => {
  const now = Date.now();
  const q = readQueue().filter(
    (e) => typeof e._enqueuedAt === "number" && now - e._enqueuedAt < QUEUE_TTL_MS
  );
  q.push(entry);
  while (q.length > QUEUE_MAX) q.shift();
  writeQueue(q);
};

const dropFromQueue = (queueId: string): void => {
  if (!queueId) return;
  const q = readQueue().filter((e) => e._queueId !== queueId);
  writeQueue(q);
};

// ───────────────────────────────────────────────────────────────────
// Transports
// ───────────────────────────────────────────────────────────────────

const apiUrl = (): string | null => {
  const base = process.env.REACT_APP_API;
  if (!base) return null;
  return `${base}logPaymentError`;
};

const withTimeout = <T>(p: Promise<T>, ms: number): Promise<T | null> =>
  new Promise((resolve) => {
    let done = false;
    const t = setTimeout(() => {
      if (done) return;
      done = true;
      resolve(null);
    }, ms);
    p.then(
      (v) => { if (!done) { done = true; clearTimeout(t); resolve(v); } },
      () => { if (!done) { done = true; clearTimeout(t); resolve(null); } }
    );
  });

// Beacon = bonus channel. Survives page unload; the browser owns the
// retry. We can't observe whether the server received it, so we don't
// trust it for queue removal — but it's our best shot when the user
// closes the tab seconds after the failure modal appears.
const sendViaBeacon = (body: string): boolean => {
  try {
    if (typeof navigator === "undefined" || !navigator.sendBeacon) return false;
    const url = apiUrl();
    if (!url) return false;
    const blob = new Blob([body], { type: "application/json" });
    return navigator.sendBeacon(url, blob);
  } catch {
    return false;
  }
};

const sendViaFetch = async (body: string): Promise<boolean> => {
  const url = apiUrl();
  if (!url) return false;
  const result = await withTimeout(
    (async () => {
      const res = await fetch(url, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body,
        keepalive: true,
      });
      return res.ok;
    })(),
    NET_TIMEOUT_MS
  );
  return result === true;
};

const sendViaFirestore = async (payload: AnyObj): Promise<boolean> => {
  const result = await withTimeout(
    (async () => {
      await addDoc(collection(db, "paymentErrors"), {
        ...payload,
        createdAt: serverTimestamp(),
      });
      return true;
    })(),
    NET_TIMEOUT_MS
  );
  return result === true;
};

// Fan out to every channel in parallel. Queue removal is driven only by
// channels that confirm delivery (fetch ok / firestore add resolved).
const sendOnce = async (payload: AnyObj): Promise<boolean> => {
  const body = JSON.stringify(payload);
  sendViaBeacon(body);
  const [fetchOk, fsOk] = await Promise.all([
    sendViaFetch(body),
    sendViaFirestore(payload),
  ]);
  return fetchOk || fsOk;
};

// ───────────────────────────────────────────────────────────────────
// Queue drain — runs on boot, on reconnect, on tab refocus.
// ───────────────────────────────────────────────────────────────────

let draining = false;
const drainQueue = async (): Promise<void> => {
  if (draining) return;
  draining = true;
  try {
    const q = readQueue();
    for (const entry of q) {
      const now = Date.now();
      if (typeof entry._enqueuedAt === "number" && now - entry._enqueuedAt >= QUEUE_TTL_MS) {
        if (entry._queueId) dropFromQueue(entry._queueId);
        continue;
      }
      const { _queueId, _enqueuedAt: _ts, ...payload } = entry;
      const ok = await sendOnce(payload);
      if (ok && _queueId) dropFromQueue(_queueId);
    }
  } finally {
    draining = false;
  }
};

export const flushPaymentErrorQueue = (): Promise<void> => drainQueue();

if (typeof window !== "undefined") {
  // Defer to keep startup work off the critical render path.
  setTimeout(() => { void drainQueue(); }, 1500);
  window.addEventListener("online", () => { void drainQueue(); });
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") void drainQueue();
    });
  }
}

// ───────────────────────────────────────────────────────────────────
// Forensic browser context
// ───────────────────────────────────────────────────────────────────

const collectBrowserContext = (): AnyObj => {
  const out: AnyObj = {};
  try {
    if (typeof navigator !== "undefined") {
      out.online = navigator.onLine;
      out.lang = navigator.language ?? null;
      const conn: any = (navigator as any).connection;
      if (conn) {
        out.connection = {
          effectiveType: conn.effectiveType ?? null,
          downlink: conn.downlink ?? null,
          rtt: conn.rtt ?? null,
          saveData: conn.saveData ?? null,
        };
      }
    }
    if (typeof document !== "undefined") {
      out.visibility = document.visibilityState;
      out.hasFocus = typeof document.hasFocus === "function" ? document.hasFocus() : null;
      out.referrer = document.referrer || null;
    }
    if (typeof performance !== "undefined") {
      out.pageAgeMs = Math.round(performance.now());
      const mem: any = (performance as any).memory;
      if (mem) {
        out.memory = {
          used: mem.usedJSHeapSize ?? null,
          total: mem.totalJSHeapSize ?? null,
          limit: mem.jsHeapSizeLimit ?? null,
        };
      }
    }
    try { out.tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* noop */ }
    if (typeof window !== "undefined") {
      out.viewport = `${window.innerWidth}x${window.innerHeight}`;
      if (window.screen) {
        out.screen = `${window.screen.width}x${window.screen.height}@${window.devicePixelRatio || 1}`;
      }
    }
  } catch { /* noop */ }
  return out;
};

// ───────────────────────────────────────────────────────────────────
// Public API
// ───────────────────────────────────────────────────────────────────

export interface PaymentErrorContext {
  source: string;
  flow?: string;
  coinInfo?: AnyObj | null;
  payamount?: number | string | null;
  amountInCrypto?: string | number | null;
  productId?: string | number | null;
  quantity?: number | null;
  chainId?: number | string | null;
  walletChainId?: number | string | null;
  walletAddress?: string | null;
  walletType?: string | null;
  votePackageId?: string | number | null;
  miningPassDurationInDays?: number | null;
  transactionType?: string | null;
  extra?: AnyObj | null;
}

// Fire-and-forget. The returned promise resolves when transports settle
// but callers should NOT await it — UI must not be blocked on telemetry.
export const logPaymentError = (
  error: any,
  ctx: PaymentErrorContext
): Promise<void> => {
  let payload: AnyObj;
  try {
    const user = (auth as any)?.currentUser;
    payload = stripUndefined({
      clientTime: new Date().toISOString(),
      userId: user?.uid ?? null,
      userEmail: user?.email ?? null,
      url: typeof window !== "undefined" ? window.location.href : null,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      source: ctx.source,
      flow: ctx.flow ?? null,
      coinInfo: ctx.coinInfo ?? null,
      payamount: ctx.payamount ?? null,
      amountInCrypto: ctx.amountInCrypto ?? null,
      productId: ctx.productId ?? null,
      quantity: ctx.quantity ?? null,
      chainId: ctx.chainId ?? null,
      walletChainId: ctx.walletChainId ?? null,
      walletAddress: ctx.walletAddress ?? null,
      walletType: ctx.walletType ?? null,
      votePackageId: ctx.votePackageId ?? null,
      miningPassDurationInDays: ctx.miningPassDurationInDays ?? null,
      transactionType: ctx.transactionType ?? null,
      error: safeSerializeError(error),
      envCheck: getPaymentEnvSnapshot(),
      browser: collectBrowserContext(),
      extra: ctx.extra ?? null,
    });
  } catch (buildErr) {
    // eslint-disable-next-line no-console
    console.warn("logPaymentError payload-build failed", buildErr);
    return Promise.resolve();
  }

  // Always console.error — captured by Safari Web Inspector (remote
  // debug from a Mac) even when every network channel is blocked.
  try {
    // eslint-disable-next-line no-console
    console.error("[paymentError]", payload.source, payload);
  } catch { /* noop */ }

  // Persist BEFORE the network attempts. Durability over latency.
  const queueId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const enqueued = { ...payload, _queueId: queueId, _enqueuedAt: Date.now() };
  try { enqueue(enqueued); } catch { /* localStorage disabled */ }
  // Stamp the payload so server-side dedupe is possible later if needed.
  payload.clientLogId = queueId;

  return (async () => {
    try {
      const ok = await sendOnce(payload);
      if (ok) dropFromQueue(queueId);
    } catch (sendErr) {
      // eslint-disable-next-line no-console
      console.warn("logPaymentError send failed", sendErr);
    }
  })();
};
